import { type ResultOf } from '@graphql-typed-document-node/core'
import { type TFunction } from 'i18next'
import * as R from 'remeda'

import { graphql } from '~/gql'

import { findByPath, pathOr } from '../generals'

import { conditionOperationLabelI18nKeyMap } from './conditionOperation'
import { matchSurveyGroupFilter } from './matchSurveyGroupFilter'
import { formatNineSegment } from './segmentHelpers'
import { type NineNumber } from './segments'
import { type SurveyGroupFilterGroups } from './surveyGroupFilterGroups'
import { type FilterText } from './types'

const surveyGroupQuestionFragment = graphql(`
  fragment BuildSurveyGroupFilterTextGroups_SurveyGroupQuestion on SurveyGroupQuestion {
    id
    name
  }
`)

const surveyGroupBrandFragment = graphql(`
  fragment BuildSurveyGroupFilterTextGroups_SurveyGroupBrand on SurveyGroupBrand {
    id
    name
  }
`)

const surveyGroupCustomerAttributeGroupFragment = graphql(`
  fragment BuildSurveyGroupFilterTextGroups_SurveyGroupCustomerAttributeGroup on SurveyGroupCustomerAttributeGroup {
    id
    name
  }
`)

/**
 * TODO: [i18n改善] t関数を引数で受け取る設計を見直す
 * 翻訳キーを返し、呼び出し側でt()を適用する形に変更することで、保守性の改善を行う
 */
type BuildSurveyGroupFilterTextGroupsParams = {
  filterGroups: SurveyGroupFilterGroups
  questions: ResultOf<typeof surveyGroupQuestionFragment>[]
  customerAttributeGroups: ResultOf<
    typeof surveyGroupCustomerAttributeGroupFragment
  >[]
  brands: ResultOf<typeof surveyGroupBrandFragment>[]
  t: TFunction
}

/**
 * サーベイグループのフィルターグループの配列をフィルターテキストグループの配列に変換します。
 *
 * @param params - 変換に必要なパラメーター。
 * @returns フィルターテキストグループの配列。
 */
export function buildSurveyGroupFilterTextGroups(
  params: BuildSurveyGroupFilterTextGroupsParams,
): FilterText[][] {
  const { brands, customerAttributeGroups, filterGroups, questions, t } = params

  return filterGroups.map((filterGroup) =>
    filterGroup.map((filter) =>
      matchSurveyGroupFilter(filter, {
        question: (question) => ({
          name: R.pipe(
            questions,
            findByPath('id', question.surveyGroupQuestionId),
            pathOr('name', t('unknown_questionnaire_2')),
          ),
          subName: t(conditionOperationLabelI18nKeyMap[question.operation]),
          values: question.answerOptions.map(R.prop('text')),
        }),
        customerAttribute: (customerAttribute) => ({
          name: R.pipe(
            customerAttributeGroups,
            findByPath(
              'id',
              customerAttribute.surveyGroupCustomerAttributeGroupId,
            ),
            pathOr('name', t('unknown_customized_segments_group_1')),
          ),
          subName: t(
            conditionOperationLabelI18nKeyMap[customerAttribute.operation],
          ),
          values: customerAttribute.customerAttributes.map(R.prop('name')),
        }),
        brandSegments: (brandSegments) => ({
          name: R.pipe(
            brands,
            findByPath('id', brandSegments.surveyGroupBrandId),
            pathOr('name', t('unknown_brand_1')),
          ),
          values: brandSegments.segmentNumbers.map((segmentNumber) =>
            formatNineSegment(segmentNumber as NineNumber, '%t1', t),
          ),
        }),
      }),
    ),
  )
}
